import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { notificationsAPI } from '../services/api';
import { Notification } from '../types';

function timeAgo(value: string) {
  const ts = new Date(value.replace(' ', 'T')).getTime();
  if (Number.isNaN(ts)) return '';
  const mins = Math.max(0, Math.floor((Date.now() - ts) / 60000));
  if (mins < 1) return 'acum';
  if (mins < 60) return `acum ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `acum ${hours}h`;
  const days = Math.floor(hours / 24);
  return `acum ${days} zile`;
}

function typeLabel(type: Notification['type']) {
  if (type === 'match') return 'Meci';
  if (type === 'open_match') return 'Provocare';
  if (type === 'booking') return 'Rezervare';
  if (type === 'tournament') return 'Turneu';
  return 'Info';
}

const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLLIElement>(null);

  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await notificationsAPI.getAll({ limit: 8 });
      if (res.success && res.data) {
        setItems(res.data.notifications || []);
        setUnreadCount(Number(res.data.unread_count || 0));
      }
    } catch {
      // Header should stay calm even if alerts fail.
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(() => load(true), 30000);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') load(true);
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [load]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (notification: Notification) => {
    if (!notification.is_read) {
      setItems(prev => prev.map(item => item.id === notification.id ? { ...item, is_read: true } : item));
      setUnreadCount(prev => Math.max(0, prev - 1));
      try { await notificationsAPI.markAsRead(notification.id); } catch { load(); }
    }
    setOpen(false);
  };

  const markAllRead = async () => {
    setItems(prev => prev.map(item => ({ ...item, is_read: true })));
    setUnreadCount(0);
    try { await notificationsAPI.markAllAsRead(); } catch { load(); }
  };

  return (
    <li className="nav-item nav-alert-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`nav-alert-btn ${open ? 'is-open' : ''}`}
        onClick={() => setOpen(prev => !prev)}
        aria-label="Notificări"
        aria-expanded={open}
      >
        <svg className="nav-alert-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M18 8.2a6 6 0 0 0-12 0c0 7.1-2.4 7.7-2.4 7.7h16.8S18 15.3 18 8.2Z" />
          <path d="M9.8 19a2.3 2.3 0 0 0 4.4 0" />
        </svg>
        {unreadCount > 0 && <span className="nav-alert-count">{unreadCount > 9 ? '9+' : unreadCount}</span>}
      </button>

      {open && (
        <div className="nav-alert-panel">
          <div className="nav-alert-head">
            <div>
              <div className="nav-alert-title">Notificări</div>
              <div className="nav-alert-subtitle">{unreadCount > 0 ? `${unreadCount} necitite` : 'Ești la zi'}</div>
            </div>
            {unreadCount > 0 && (
              <button type="button" className="nav-alert-clear" onClick={markAllRead}>
                Marchează toate
              </button>
            )}
          </div>

          {loading && items.length === 0 ? (
            <div className="nav-alert-empty">Se încarcă...</div>
          ) : items.length === 0 ? (
            <div className="nav-alert-empty">Nu ai notificări încă.</div>
          ) : (
            <div className="nav-alert-list">
              {items.map(item => {
                const content = (
                  <>
                    <div className="nav-alert-row-top">
                      <span className="nav-alert-type">{typeLabel(item.type)}</span>
                      <span className="nav-alert-time">{timeAgo(item.created_at)}</span>
                    </div>
                    <div className="nav-alert-item-title">{item.title}</div>
                    <div className="nav-alert-message">{item.message}</div>
                  </>
                );
                const className = `nav-alert-item ${item.is_read ? '' : 'is-unread'}`;
                return item.action_url ? (
                  <Link key={item.id} to={item.action_url} className={className} onClick={() => markRead(item)}>
                    {content}
                  </Link>
                ) : (
                  <button key={item.id} type="button" className={className} onClick={() => markRead(item)}>
                    {content}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </li>
  );
};

export default NotificationBell;
